import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckCircle2, KeyRound, Store, Loader2 } from "lucide-react";
import { toast } from "sonner";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const publicApi = axios.create({ baseURL: API_BASE });

// Publieke webshop-URL (waar geactiveerde klanten daarna heen gaan).
const WEBSHOP_LOGIN_URL = "https://nextproduct.nl";

// Vertaalstrings voor de activatiepagina. Ondersteund: nl/en/de.
const I18N = {
  nl: {
    badge: "Bestelportaal-activatie",
    welcome: "Welkom",
    intro_no_extra: "Stel je wachtwoord in om je account te activeren.",
    intro_with_extra: "Stel je wachtwoord in en vul je bedrijfsgegevens aan om je account te activeren.",
    email: "E-mail",
    voornaam: "Voornaam", achternaam: "Achternaam", telefoon_opt: "Telefoon (optioneel)",
    pw_title: "Wachtwoord instellen",
    pw_new: "Nieuw wachtwoord", pw_repeat: "Herhaal wachtwoord",
    pw_hint: "Minimaal 8 tekens.",
    company_title: "Bedrijfsgegevens aanvullen",
    company_hint: "Deze gegevens ontbreken nog in ons systeem. Vul ze nu in zodat we correct kunnen factureren.",
    company_hint_prefilled: "We hebben je gegevens al zoveel mogelijk ingevuld. Controleer ze en pas aan waar nodig.",
    huisnummer: "Huisnummer",
    btw_hint_buitenland: "Verplicht voor klanten buiten Nederland.",
    btw_hint_nl: "Optioneel — vul in als je op BTW-nummer wilt factureren.",
    kvk: "KvK-nummer", btw: "BTW-nummer",
    straat: "Straat", postcode: "Postcode", plaats: "Plaats", land: "Land",
    submit: "Account activeren", busy: "Bezig…",
    err_pw_short: "Wachtwoord minimaal 8 tekens",
    err_pw_mismatch: "Wachtwoorden komen niet overeen",
    err_name_required: "Naam is verplicht",
    err_activate: "Activeren mislukt",
    invalid_title: "Uitnodiging niet geldig",
    success_title: "Account geactiveerd!",
    success_body: "Je kunt nu inloggen op het bestelportaal met",
    klantcode_label: "Je klantnummer",
    go_login: "Naar inloggen",
  },
  en: {
    badge: "Ordering-portal activation",
    welcome: "Welcome",
    intro_no_extra: "Set your password to activate your account.",
    intro_with_extra: "Set your password and complete your company details to activate your account.",
    email: "Email",
    voornaam: "First name", achternaam: "Last name", telefoon_opt: "Phone (optional)",
    pw_title: "Set password",
    pw_new: "New password", pw_repeat: "Repeat password",
    pw_hint: "At least 8 characters.",
    company_title: "Complete company details",
    company_hint: "These details are still missing in our system. Please enter them so we can invoice correctly.",
    company_hint_prefilled: "We have pre-filled what we know. Please check and correct where needed.",
    huisnummer: "Number",
    btw_hint_buitenland: "Required for customers outside the Netherlands.",
    btw_hint_nl: "Optional — enter it if you want invoices with your VAT number.",
    kvk: "Chamber-of-Commerce number", btw: "VAT number",
    straat: "Street", postcode: "Postal code", plaats: "City", land: "Country",
    submit: "Activate account", busy: "Working…",
    err_pw_short: "Password must be at least 8 characters",
    err_pw_mismatch: "Passwords do not match",
    err_name_required: "Name is required",
    err_activate: "Activation failed",
    invalid_title: "Invitation not valid",
    success_title: "Account activated!",
    success_body: "You can now sign in to the ordering portal with",
    klantcode_label: "Your customer number",
    go_login: "Go to sign-in",
  },
  de: {
    badge: "Bestellportal-Aktivierung",
    welcome: "Willkommen",
    intro_no_extra: "Legen Sie Ihr Passwort fest, um Ihr Konto zu aktivieren.",
    intro_with_extra: "Legen Sie Ihr Passwort fest und ergänzen Sie Ihre Firmendaten, um Ihr Konto zu aktivieren.",
    email: "E-Mail",
    voornaam: "Vorname", achternaam: "Nachname", telefoon_opt: "Telefon (optional)",
    pw_title: "Passwort festlegen",
    pw_new: "Neues Passwort", pw_repeat: "Passwort wiederholen",
    pw_hint: "Mindestens 8 Zeichen.",
    company_title: "Firmendaten ergänzen",
    company_hint: "Diese Angaben fehlen noch in unserem System. Bitte tragen Sie sie ein, damit wir korrekt in Rechnung stellen können.",
    company_hint_prefilled: "Wir haben Ihre Daten bereits so weit wie möglich ausgefüllt. Bitte prüfen und ggf. anpassen.",
    huisnummer: "Hausnummer",
    btw_hint_buitenland: "Für Kunden außerhalb der Niederlande erforderlich.",
    btw_hint_nl: "Optional — geben Sie sie an, wenn Rechnungen Ihre USt-IdNr. enthalten sollen.",
    kvk: "Handelsregisternummer", btw: "USt-IdNr.",
    straat: "Straße", postcode: "Postleitzahl", plaats: "Ort", land: "Land",
    submit: "Konto aktivieren", busy: "Verarbeitung…",
    err_pw_short: "Passwort muss mindestens 8 Zeichen lang sein",
    err_pw_mismatch: "Passwörter stimmen nicht überein",
    err_name_required: "Name ist erforderlich",
    err_activate: "Aktivierung fehlgeschlagen",
    invalid_title: "Einladung nicht gültig",
    success_title: "Konto aktiviert!",
    success_body: "Sie können sich jetzt im Bestellportal anmelden mit",
    klantcode_label: "Ihre Kundennummer",
    go_login: "Zur Anmeldung",
  },
};

/** Publieke activatiepagina voor Webshop-uitnodigingen. Deze route valt buiten de
 *  Protected-layout: geen CRM-login vereist. */
export default function WebshopActiveren() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [org, setOrg] = useState(null);
  const [email, setEmail] = useState("");
  const [vereist, setVereist] = useState([]);
  const [taal, setTaal] = useState("nl");
  const [done, setDone] = useState(false);
  const [klantcode, setKlantcode] = useState("");
  const t = I18N[taal] || I18N.nl;
  const [f, setF] = useState({
    voornaam: "", achternaam: "", telefoon: "",
    wachtwoord: "", wachtwoord2: "",
    kvk: "", btw_nummer: "",
    straat: "", huisnummer: "", postcode: "", plaats: "",
  });

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const { data } = await publicApi.get(`/shop/uitnodiging/${token}`);
        if (!live) return;
        setEmail(data.email);
        setOrg(data.organisatie);
        setVereist(data.vereiste_velden || []);
        setTaal(["nl", "en", "de"].includes(data.taal) ? data.taal : "nl");
        setF(prev => ({
          ...prev,
          voornaam: data.contact?.voornaam || "",
          achternaam: data.contact?.achternaam || "",
          telefoon: data.contact?.telefoon || "",
          kvk: data.organisatie.kvk || "",
          btw_nummer: data.organisatie.btw_nummer || "",
          straat: data.organisatie.straat || "",
          huisnummer: data.organisatie.huisnummer || "",
          postcode: data.organisatie.postcode || "",
          plaats: data.organisatie.plaats || "",
        }));
      } catch (e) {
        setError(e?.response?.data?.detail || "Uitnodiging niet gevonden");
      } finally { if (live) setLoading(false); }
    })();
    return () => { live = false; };
  }, [token]);

  const set = (k, v) => setF(prev => ({ ...prev, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (f.wachtwoord.length < 8) { toast.error(t.err_pw_short); return; }
    if (f.wachtwoord !== f.wachtwoord2) { toast.error(t.err_pw_mismatch); return; }
    if (!f.voornaam.trim() || !f.achternaam.trim()) { toast.error(t.err_name_required); return; }
    setBusy(true);
    try {
      const { data: res } = await publicApi.post(`/shop/uitnodiging/${token}/activate`, {
        wachtwoord: f.wachtwoord,
        voornaam: f.voornaam.trim(),
        achternaam: f.achternaam.trim(),
        telefoon: f.telefoon.trim(),
        kvk: f.kvk.trim(),
        btw_nummer: f.btw_nummer.trim(),
        straat: f.straat.trim(),
        huisnummer: f.huisnummer.trim(),
        postcode: f.postcode.trim(),
        plaats: f.plaats.trim(),
      });
      setKlantcode(res?.klantcode || "");
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || t.err_activate);
    } finally { setBusy(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow p-8 text-center border">
          <div className="text-red-500 text-4xl mb-3">✕</div>
          <h1 className="text-xl font-semibold mb-2">{t.invalid_title}</h1>
          <p className="text-muted-foreground text-sm">{error}</p>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow p-8 text-center border">
          <CheckCircle2 className="h-14 w-14 text-emerald-500 mx-auto mb-3" />
          <h1 className="text-xl font-semibold mb-2">{t.success_title}</h1>
          <p className="text-muted-foreground text-sm mb-6">
            {t.success_body} <strong>{email}</strong>.
          </p>
          {klantcode && <p className="text-sm text-muted-foreground mb-6" data-testid="activatie-klantcode">
            {t.klantcode_label || "Klantnummer"}: <strong className="font-mono text-foreground">{klantcode}</strong>
          </p>}
          <Button
            onClick={() => { window.location.href = WEBSHOP_LOGIN_URL; }}
            data-testid="btn-naar-login">{t.go_login}</Button>
        </div>
      </div>
    );
  }

  const buitenland = ((org?.land || "Nederland").toLowerCase() !== "nederland");

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 py-8 px-4">
      <div className="max-w-2xl mx-auto" data-testid="activeer-form">
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 bg-white rounded-full px-4 py-1.5 shadow-sm border">
            <Store className="h-4 w-4 text-primary" />
            <span className="text-xs font-medium">{t.badge}</span>
          </div>
          <h1 className="text-3xl font-semibold mt-4">{t.welcome} {org?.naam}</h1>
          <p className="text-muted-foreground text-sm mt-1">
            {vereist.length > 0 ? t.intro_with_extra : t.intro_no_extra}
          </p>
        </div>

        <form onSubmit={submit} className="bg-white rounded-2xl shadow-sm border p-6 space-y-5">
          <div>
            <Label className="text-xs uppercase tracking-wide text-muted-foreground">{t.email}</Label>
            <Input value={email} disabled className="bg-muted" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>{t.voornaam} *</Label>
              <Input value={f.voornaam} onChange={e => set("voornaam", e.target.value)} required data-testid="inp-voornaam" />
            </div>
            <div>
              <Label>{t.achternaam} *</Label>
              <Input value={f.achternaam} onChange={e => set("achternaam", e.target.value)} required data-testid="inp-achternaam" />
            </div>
          </div>
          <div>
            <Label>{t.telefoon_opt}</Label>
            <Input value={f.telefoon} onChange={e => set("telefoon", e.target.value)} data-testid="inp-telefoon" />
          </div>

          <div className="border-t pt-4">
            <div className="flex items-center gap-2 text-sm font-medium mb-3">
              <KeyRound className="h-4 w-4" /> {t.pw_title}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>{t.pw_new} *</Label>
                <Input type="password" value={f.wachtwoord} onChange={e => set("wachtwoord", e.target.value)}
                  minLength={8} required data-testid="inp-wachtwoord" />
              </div>
              <div>
                <Label>{t.pw_repeat} *</Label>
                <Input type="password" value={f.wachtwoord2} onChange={e => set("wachtwoord2", e.target.value)}
                  minLength={8} required data-testid="inp-wachtwoord2" />
              </div>
            </div>
            <p className="text-[11px] text-muted-foreground mt-1">{t.pw_hint}</p>
          </div>

          <div className="border-t pt-4">
            <div className="text-sm font-medium mb-1">{t.company_title}</div>
            <p className="text-xs text-muted-foreground mb-3">
              {vereist.length > 0 ? t.company_hint : t.company_hint_prefilled}
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2 md:col-span-1">
                <Label>{t.kvk}</Label>
                <Input value={f.kvk} onChange={e => set("kvk", e.target.value)} data-testid="inp-kvk" />
              </div>
              <div className="col-span-2 md:col-span-1">
                <Label>{t.btw}{buitenland ? " *" : ""}</Label>
                <Input value={f.btw_nummer} onChange={e => set("btw_nummer", e.target.value)}
                  required={buitenland} data-testid="inp-btw" />
                <p className="text-[11px] text-muted-foreground mt-1">
                  {buitenland ? t.btw_hint_buitenland : t.btw_hint_nl}
                </p>
              </div>
              <div className="col-span-2 md:col-span-1">
                <Label>{t.straat} *</Label>
                <Input value={f.straat} onChange={e => set("straat", e.target.value)} required data-testid="inp-straat" />
              </div>
              <div className="col-span-2 md:col-span-1">
                <Label>{t.huisnummer}</Label>
                <Input value={f.huisnummer} onChange={e => set("huisnummer", e.target.value)} data-testid="inp-huisnummer" />
              </div>
              <div className="col-span-2 md:col-span-1">
                <Label>{t.postcode} *</Label>
                <Input value={f.postcode} onChange={e => set("postcode", e.target.value)} required data-testid="inp-postcode" />
              </div>
              <div className="col-span-2 md:col-span-1">
                <Label>{t.plaats} *</Label>
                <Input value={f.plaats} onChange={e => set("plaats", e.target.value)} required data-testid="inp-plaats" />
              </div>
              <div className="col-span-2">
                <Label>{t.land || "Land"}</Label>
                <Input value={org?.land || ""} disabled className="bg-muted" data-testid="inp-land" />
              </div>
            </div>
          </div>

          <Button type="submit" disabled={busy} className="w-full h-11 text-base" data-testid="btn-activeer">
            {busy ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{t.busy}</>
                  : t.submit}
          </Button>
        </form>
      </div>
    </div>
  );
}
